import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OmegaBrainStatusCard — unified "all-in-one brain" health card.
 *
 * Surfaces the 5 Omega engines (Monte Carlo, Claude reasoning, Guardian,
 * Auto-Trim, Replay Digest) in one compact grid. Polls every 30 s. Toggles
 * for Guardian + Auto-Trim are inline so the user can flip them without
 * leaving the card.
 *
 * Variants:
 *   • compact={false} (default) — full card, fits desktop dashboard.
 *   • compact={true}            — denser layout for mobile expanded views.
 */

const ENGINE_ORDER = ["simulation", "reasoning", "guardian", "autotrim", "replay_digest"];
const ENGINE_ICONS = {
  simulation: "Σ",
  reasoning: "Ψ",
  guardian: "◉",
  autotrim: "✂",
  replay_digest: "↻",
};
const ENGINE_ACCENTS = {
  simulation: "#60a5fa",
  reasoning: "#a78bfa",
  guardian: "#4ade80",
  autotrim: "#fbbf24",
  replay_digest: "#22d3ee",
};

function fmtRelative(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return null; }
}

export default function OmegaBrainStatusCard({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/omega/brain/status`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const toggle = async (engine) => {
    setToggling(engine);
    try {
      const path = engine === "guardian" ? "/omega/guardian/toggle" : "/omega/autotrim/toggle";
      await axios.post(`${API}${path}`);
      await load();
    } catch {} finally { setToggling(null); }
  };

  const engines = data?.engines || {};
  const overall = data?.overall || (loading ? "loading" : "unknown");
  const overallColor =
    overall === "all_systems_go" ? "#4ade80" :
    overall === "degraded" ? "#fbbf24" :
    overall === "partial" ? "#f87171" : "#8b949e";
  const overallLabel = {
    all_systems_go: "ALL SYSTEMS GO",
    degraded: "DEGRADED",
    partial: "PARTIAL",
    loading: "LOADING…",
    unknown: "STATUS UNKNOWN",
  }[overall];

  const padCard = compact ? 14 : 18;

  return (
    <div className="card" data-testid="omega-brain-status-card" style={{
      borderColor: "rgba(167,139,250,0.3)",
      background: "linear-gradient(165deg, rgba(167,139,250,0.04), rgba(96,165,250,0.02))",
      padding: padCard,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 12,
            background: "linear-gradient(135deg, #a78bfa, #60a5fa)",
            display: "grid", placeItems: "center",
            fontSize: 18, fontWeight: 800, color: "#fff",
            boxShadow: "0 6px 18px rgba(167,139,250,0.35)",
          }}>Ω</div>
          <div>
            <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 2, fontWeight: 700 }}>OMEGA ALL-IN-ONE BRAIN</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
              {data?.healthyCount != null ? `${data.healthyCount}/${data.totalEngines} engines healthy` : "Checking engines…"}
            </div>
          </div>
        </div>
        <div data-testid="brain-overall-pill" style={{
          padding: "5px 11px", borderRadius: 999, fontSize: 9,
          fontWeight: 800, letterSpacing: 1.5,
          color: overallColor,
          background: `${overallColor}1F`,
          border: `1px solid ${overallColor}55`,
        }}>{overallLabel}</div>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {ENGINE_ORDER.map(key => {
          const e = engines[key];
          const accent = ENGINE_ACCENTS[key];
          const icon = ENGINE_ICONS[key];
          const isToggleable = key === "guardian" || key === "autotrim";
          const lastEvt = e ? fmtRelative(e.lastEventAt) : null;
          return (
            <div key={key} data-testid={`brain-engine-${key}`} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: compact ? "10px 12px" : "12px 14px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.025)",
              border: `1px solid ${e?.healthy ? "rgba(255,255,255,0.06)" : "rgba(248,113,113,0.3)"}`,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 10, flex: "0 0 auto",
                background: `${accent}1A`, color: accent,
                display: "grid", placeItems: "center",
                fontSize: 16, fontWeight: 700,
              }}>{icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{e?.label || key}</span>
                  <span data-testid={`brain-engine-${key}-state`} style={{
                    fontSize: 8, fontWeight: 800, letterSpacing: 1,
                    padding: "2px 6px", borderRadius: 4,
                    background: e?.enabled ? `${accent}26` : "rgba(255,255,255,0.06)",
                    color: e?.enabled ? accent : "var(--dim)",
                    border: `1px solid ${e?.enabled ? accent + "55" : "rgba(255,255,255,0.1)"}`,
                  }}>{e?.enabled ? "ON" : "STANDBY"}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e?.detail || (loading ? "loading…" : "—")}
                  {lastEvt && <span style={{ marginLeft: 6, opacity: 0.7 }}>· {lastEvt}</span>}
                </div>
              </div>
              {isToggleable && (
                <button
                  data-testid={`brain-toggle-${key}`}
                  onClick={() => toggle(key)}
                  disabled={toggling === key}
                  style={{
                    flex: "0 0 auto",
                    width: 42, height: 24, borderRadius: 999, border: "none", padding: 0,
                    background: e?.enabled ? `linear-gradient(135deg, ${accent}, ${accent}cc)` : "var(--layer2)",
                    cursor: toggling === key ? "wait" : "pointer",
                    position: "relative",
                    opacity: toggling === key ? 0.6 : 1,
                    transition: "background 200ms ease",
                  }}
                  aria-pressed={!!e?.enabled}
                  aria-label={`Toggle ${e?.label || key}`}
                >
                  <span style={{
                    position: "absolute", top: 3, left: e?.enabled ? 21 : 3,
                    width: 18, height: 18, borderRadius: "50%", background: "#fff",
                    transition: "left 200ms ease",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                  }} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10, fontSize: 10, color: "var(--dim)", textAlign: "center", letterSpacing: 0.5 }}>
        {data?.version || "omega-brain-v3"} · auto-refreshes every 30s
      </div>
    </div>
  );
}
